import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { useSession } from "../../lib/session";
import { AssetDrawer, AssetTile, type AssetCard } from "./AssetViews";

type Product = components["schemas"]["ProductOut"];
const TABS = ["all", "raw", "selects", "ai", "final"] as const;
type Tab = (typeof TABS)[number];
const TYPES = ["RAW", "BROLL", "TESTIMONIAL", "INTERVIEW", "SELECT", "FINAL", "REFERENCE", "PRODUCT_ASSET"] as const;
const APPROVALS = ["UNREVIEWED", "APPROVED", "REJECTED", "RESTRICTED"] as const;

/** Asset Library (doc 04 P26): an index over Prisma's records, never a NAS folder browser. */
export function AssetLibraryPage() {
  const { t } = useTranslation();
  const me = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(params.get("tab") ?? "") ? (params.get("tab") as Tab) : "all";
  const [text, setText] = useState(params.get("q") ?? "");
  const open = params.get("asset");
  const filters = {
    tab,
    product: params.get("product") || undefined,
    asset_type: params.get("asset_type") || undefined,
    approval: params.get("approval") || undefined,
    q: params.get("q") || undefined,
  };
  const q = useQuery({ queryKey: ["assets", filters], queryFn: () => call<AssetCard[]>(api.GET("/app/v1/assets", { params: { query: filters } })) });
  const products = useQuery({ queryKey: ["products"], queryFn: () => call<Product[]>(api.GET("/app/v1/products")),
    enabled: Boolean(me.permissions["product.view"]) });
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const select = "h-10 rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink";
  return (
    <div className="space-y-6">
      <PageHeader titleKey="assets.title" subtitle={t("assets.subtitle")} />
      <div role="tablist" aria-label={t("assets.tabs_label")} className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((k) => (
          <button key={k} role="tab" type="button" aria-selected={tab === k} onClick={() => set("tab", k === "all" ? null : k)}
            className="-mb-px border-b-2 border-transparent px-4 py-2.5 text-[14px] text-ink-2 hover:text-ink aria-selected:border-[var(--accent-primary)] aria-selected:font-semibold aria-selected:text-ink">
            {t(`assets.tab_${k}`)}
          </button>
        ))}
      </div>
      <form className="flex flex-wrap items-center gap-3" onSubmit={(e) => { e.preventDefault(); set("q", text.trim() || null); }}>
        <label className="relative">
          <span className="sr-only">{t("assets.search")}</span>
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-2" />
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("assets.search")}
            className="h-10 w-64 rounded-[var(--radius-control)] border border-line bg-surface pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-2" />
        </label>
        <label className="sr-only" htmlFor="al-product">{t("requests.col_product")}</label>
        <select id="al-product" className={select} value={filters.product ?? ""} onChange={(e) => set("product", e.target.value || null)}>
          <option value="">{t("requests.all_products")}</option>
          {(products.data ?? []).map((p) => <option key={p.id} value={p.code}>{p.name}</option>)}
        </select>
        <label className="sr-only" htmlFor="al-type">{t("assets.asset_type")}</label>
        <select id="al-type" className={select} value={filters.asset_type ?? ""} onChange={(e) => set("asset_type", e.target.value || null)}>
          <option value="">{t("assets.all_types")}</option>
          {TYPES.map((x) => <option key={x} value={x}>{t(`assets.type_${x.toLowerCase()}`)}</option>)}
        </select>
        <label className="sr-only" htmlFor="al-approval">{t("assets.approval")}</label>
        <select id="al-approval" className={select} value={filters.approval ?? ""} onChange={(e) => set("approval", e.target.value || null)}>
          <option value="">{t("assets.all_approvals")}</option>
          {APPROVALS.map((x) => <option key={x} value={x}>{t(`assets.approval_${x.toLowerCase()}`)}</option>)}
        </select>
      </form>
      <QueryState query={q} emptyKey="assets.empty">
        {(list) => (
          <ul className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {list.map((a) => <AssetTile key={a.id} a={a} onOpen={(id) => set("asset", id)} />)}
          </ul>
        )}
      </QueryState>
      <AssetDrawer id={open} onClose={() => set("asset", null)} onOpen={(id) => set("asset", id)} />
    </div>
  );
}
